import { trimEnd, trimStart } from 'lodash-es';
import q from 'querystring';
import { API_MOCK_URL, DOWNLOAD_BASE_URL } from '../constants';
import { DownloadableFileLink } from '../definition';

const HTTP_REGEX = new RegExp(
  '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$',
  'i'
);

export function toUrlQueryString(data: Record<string, any>) {
  return q.stringify(data);
}

export function parseUrlQueryString<T extends Record<string, any> = any>(
  url?: string
) {
  return (url ? q.parse(url.split('?')[1]) : {}) as T;
}

export function toUrl(url: string, params?: Record<string, any>): string {
  const sep = url.includes('?') ? '&' : '?';
  const queryString = toUrlQueryString(params ?? {});
  if (queryString.length) return `${url}${sep}${queryString}`;
  return `${url}`;
}

export function urlJoin(...paths: (string | null | undefined)[]): string {
  return paths
    .filter(Boolean)
    .map((p) =>
      p?.endsWith('://') ? p : trimStart(trimEnd(p as string, '/'), '/')
    )
    .filter((x) => x !== '/')
    .join('/')
    .replace('///', '//');
}

export function toValidDownloadUrl(url?: string) {
  if (!url) return url;
  if (!url.startsWith('http')) url = urlJoin(DOWNLOAD_BASE_URL, url);
  return url;
}

export function isValidDownloadLink(value?: DownloadableFileLink) {
  return !!(value?.excel || value?.csv || value?.tsv);
}

export function urlParamify(
  url: string,
  params?: Record<string, any>,
  queryString?: Record<string, any>
) {
  if (params)
    Object.keys(params).forEach((k) => {
      url = toUrl(url.replaceAll(`:${k}`, String(params[k])), queryString);
    });

  return url;
}

export const toRoute = urlParamify;

export function urlToValidHttps(url?: string) {
  if (!url) return url;
  if (!url.startsWith('http')) url = urlJoin('https://', url);

  return url;
}

export function urlMock(path: string, json = true) {
  return urlJoin(API_MOCK_URL, `${path}${json ? '.json' : ''}`);
}

export const isValidDomain = (url: string) => {
  return HTTP_REGEX.test(url);
};

export function trimCurrentHostname(url?: string) {
  return url?.replace(window.origin, '') || '';
}

/**
 * Upper bound on the length (in characters) that `safeDecodeURIComponent`
 * will attempt to decode. Anything larger is returned as-is to keep the
 * main thread responsive. 1 MB is far above any legitimate user paste
 * in this app (the largest expected input is a few hundred KB of URLs).
 */
const SAFE_DECODE_MAX_INPUT_LENGTH = 1_000_000;

/**
 * Safely percent-decode a string.
 *
 * Wraps native `decodeURIComponent` so a malformed `%XX` group (e.g. a stray
 * `%`, or an invalid UTF-8 sequence like `%80%8b`) doesn't throw
 * `URIError: URI malformed`. When decoding fails, the original value is
 * returned unchanged.
 *
 * Security notes for callers:
 *  1. Never pass the result to `innerHTML` / `dangerouslySetInnerHTML`.
 *     Rendering it as a JSX text/attribute child is fine — React escapes.
 *  2. Never feed the result into another decoder. A second decode can turn
 *     a benign-looking `%252E%252E%2F` into `../` (path traversal).
 *  3. When the value flows back into a URL path/query, re-encode with
 *     `encodeURIComponent` at that boundary.
 *  4. The output may contain control characters (e.g. `\u0000` from `%00`).
 *     Sanitize before passing into sinks that treat them specially (logs,
 *     shell commands, C-string APIs, etc.).
 */
export function safeDecodeURIComponent(value: string): string {
  if (typeof value !== 'string') return '';
  if (value.length > SAFE_DECODE_MAX_INPUT_LENGTH) return value;
  try {
    return decodeURIComponent(value);
  } catch (err) {
    if (err instanceof URIError) return value;
    throw err;
  }
}

export function decodeQueryParamValues(encoded?: string, separator?: string) {
  return encoded?.replace(/\^$/, '').split(separator ?? '^') || [];
}

export function encodeQueryParamValues(decoded?: string[], separator?: string) {
  return decoded?.join(separator ?? '^') || undefined;
}

export function isValidUrl(value: string) {
  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return true;
  } catch (err) {
    return false;
  }
}

export function isValidCompanyLinkedInUrl(url: string) {
  return /^(https?:\/\/)?(www\.)?linkedin\.com\/company\/[^/]+\/?$/.test(
    url.trim()
  );
}

export function isValidPeopleLinkedInUrl(url: string) {
  return /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[^/]+\/?$/.test(url.trim());
}

export function cleanUrl(url: string) {
  return url
    .replace(/(^\w+:|^)\/\//, '')
    .replace(/^www./, '')
    .replace(/\/$/, '');
}

export function removePathFramUrl(url: string) {
  return url.replace(/(\.[a-z]{2,})[/?#].*$/, '$1'); // Keep only the domain, remove everything after TLD (paths, query params, fragments)
}

export function isValidCompanyDomain(url: string) {
  return /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,6}$/.test(
    url.trim()
  );
}
